/**
 * AdminUsers (admin only) — provision staff/managers and map RingCentral
 * extensions. Talks to the live server endpoints (POST/GET /api/admin/users).
 * The server creates the auth user + profile + extension mappings and returns a
 * one-time temporary password the admin shares with the new user.
 *
 * Offboarding is SOFT deactivate (POST /api/admin/users/:id/active): the
 * profile + their calls/scores/coaching history stay intact, but the auth
 * middleware rejects them on every request. Two-step inline confirm; never
 * available on your own row (the server also refuses self-deactivation).
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiRequestError, type CreateUserResult } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { useSimulation } from "@/auth/SimulationProvider";
import type { AppRole } from "@/types";
import { PageHeader, Button, TextField, SelectField, Card, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { EmptyState } from "@/components/EmptyState";

const ROLE_OPTIONS: Array<{ value: AppRole; label: string }> = [
  { value: "staff", label: "Staff (rep)" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
];

function roleTone(role: AppRole): "muted" | "primary" | "warning" {
  return role === "admin" ? "warning" : role === "manager" ? "primary" : "muted";
}

export function AdminUsers() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { simulate } = useSimulation();
  const { user } = useAuth();

  const usersQuery = useQuery({
    queryKey: ["admin", "users"],
    queryFn: ({ signal }) => api.adminListUsers(signal),
  });

  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(
    null,
  );
  const resetPw = useMutation({
    mutationFn: (u: { id: string; email: string }) =>
      api.adminResetPassword(u.id).then((r) => ({ ...r, email: u.email })),
    onSuccess: (r) => setResetResult({ email: r.email, password: r.temp_password }),
  });

  // Toggle the per-user live-monitor permission (optimistic-ish: refetch on done).
  const liveMonitor = useMutation({
    mutationFn: (u: { id: string; enabled: boolean }) => api.adminSetLiveMonitor(u.id, u.enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  // Soft deactivate/reactivate. Two-step inline confirm (no native dialogs —
  // matches the rest of the panel): the first click arms the row, the second
  // confirms. Deactivation keeps the user's calls/scores/coaching history; it
  // just locks them out on every request. The server refuses self-deactivation
  // (and the button is hidden for the signed-in admin anyway).
  const [pendingActive, setPendingActive] = useState<{ id: string; active: boolean } | null>(
    null,
  );
  const setActive = useMutation({
    mutationFn: (u: { id: string; active: boolean }) => api.adminSetUserActive(u.id, u.active),
    onSuccess: () => {
      setPendingActive(null);
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<AppRole>("staff");
  const [extensions, setExtensions] = useState("");
  const [result, setResult] = useState<CreateUserResult | null>(null);

  const createUser = useMutation({
    mutationFn: () =>
      api.adminCreateUser({
        email: email.trim(),
        full_name: fullName.trim(),
        role,
        extension_numbers: extensions
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: (res) => {
      setResult(res);
      setEmail("");
      setFullName("");
      setRole("staff");
      setExtensions("");
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setResult(null);
    createUser.mutate();
  };

  const errMsg =
    createUser.error instanceof ApiRequestError
      ? createUser.error.body?.error === "email_exists"
        ? "That email already has an account."
        : createUser.error.message
      : createUser.error
        ? "Something went wrong creating the user."
        : null;

  const users = usersQuery.data?.users ?? [];

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title="Users & extensions"
        subtitle="Create reps and managers, and map their RingCentral extension numbers."
      />

      <div className="grid gap-6 p-5 lg:grid-cols-[360px_1fr]">
        {/* Create form */}
        <Card className="h-fit">
          <h2 className="mb-3 text-sm font-semibold">Add a user</h2>
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <TextField
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="rep@tintking.net"
            />
            <TextField
              label="Full name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Rep"
            />
            <SelectField
              label="Role"
              options={ROLE_OPTIONS}
              value={role}
              onChange={(e) => setRole(e.target.value as AppRole)}
            />
            <TextField
              label="RingCentral extensions (comma-separated)"
              value={extensions}
              onChange={(e) => setExtensions(e.target.value)}
              placeholder="1042, 1043"
            />
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending ? "Creating…" : "Create user"}
            </Button>
            {errMsg && <p className="text-sm text-red-600">{errMsg}</p>}
          </form>

          {result && (
            <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-800 dark:bg-emerald-950/40">
              <p className="font-medium text-emerald-800 dark:text-emerald-300">
                Created {result.email} ({result.role})
              </p>
              <p className="mt-1">
                Temporary password:{" "}
                <code className="rounded bg-background px-1.5 py-0.5 font-mono">
                  {result.temp_password}
                </code>
              </p>
              <p className="mt-1 text-muted-foreground">
                Share it with them and have them change it after first login.
              </p>
              {result.warning && (
                <p className="mt-1 text-amber-700 dark:text-amber-400">{result.warning}</p>
              )}
            </div>
          )}
        </Card>

        {/* Users list */}
        <div className="flex flex-col">
          <h2 className="mb-3 text-sm font-semibold">
            Team {users.length > 0 && <span className="text-muted-foreground">({users.length})</span>}
          </h2>
          {resetResult && (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
              New password for <span className="font-medium">{resetResult.email}</span>:{" "}
              <code className="rounded bg-background px-1.5 py-0.5 font-mono">
                {resetResult.password}
              </code>
              <span className="ml-1 text-muted-foreground">— share it; they should change it.</span>
            </div>
          )}
          {setActive.isError && (
            <p className="mb-3 text-sm text-red-600">
              Couldn&apos;t update that user&apos;s access. Please try again.
            </p>
          )}
          {usersQuery.isLoading && <LoadingState label="Loading users…" />}
          {usersQuery.isError && (
            <ErrorState error={usersQuery.error} onRetry={() => void usersQuery.refetch()} />
          )}
          {!usersQuery.isLoading && !usersQuery.isError && users.length === 0 && (
            <EmptyState
              title="No users yet"
              description="Create your first rep with the form on the left."
            />
          )}
          {users.length > 0 && (
            <Card className="overflow-hidden p-0">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Email</th>
                    <th className="px-4 py-2 font-medium">Role</th>
                    <th className="px-4 py-2 font-medium">Extensions</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Live monitor</th>
                    <th className="px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      // Deactivated users are visually muted (plus the badge).
                      className={
                        "border-b last:border-0" + (u.active ? "" : " opacity-60")
                      }
                    >
                      <td className="px-4 py-2">{u.full_name ?? "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-2">
                        <Badge tone={roleTone(u.role)}>{u.role}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        {u.extensions.length ? u.extensions.join(", ") : "—"}
                      </td>
                      <td className="px-4 py-2">
                        {u.active ? (
                          <Badge tone="success">active</Badge>
                        ) : (
                          <Badge tone="danger">deactivated</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="ghost"
                          className="px-2 py-1 text-xs"
                          disabled={liveMonitor.isPending}
                          title="Toggle access to the live-call monitor (firehose)"
                          onClick={() =>
                            liveMonitor.mutate({ id: u.id, enabled: !u.can_view_live_monitor })
                          }
                        >
                          <Badge tone={u.can_view_live_monitor ? "success" : "muted"}>
                            {u.can_view_live_monitor ? "on" : "off"}
                          </Badge>
                        </Button>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-2">
                          {/* Simulating a deactivated user would just 403 on
                              every request (auth middleware), so hide it. */}
                          {u.id !== user?.id && u.active && (
                            <Button
                              variant="ghost"
                              className="px-2 py-1 text-xs"
                              onClick={() => {
                                simulate({
                                  id: u.id,
                                  email: u.email,
                                  full_name: u.full_name,
                                  role: u.role,
                                });
                                navigate("/calls");
                              }}
                            >
                              Simulate
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            className="px-2 py-1 text-xs"
                            disabled={resetPw.isPending}
                            onClick={() => {
                              setResetResult(null);
                              resetPw.mutate({ id: u.id, email: u.email });
                            }}
                          >
                            Reset password
                          </Button>
                          {/* Deactivate/reactivate — never for yourself. */}
                          {u.id !== user?.id &&
                            (pendingActive?.id === u.id ? (
                              <>
                                <Button
                                  variant="ghost"
                                  className={
                                    "px-2 py-1 text-xs font-semibold " +
                                    (pendingActive.active
                                      ? "text-emerald-600"
                                      : "text-red-600")
                                  }
                                  disabled={setActive.isPending}
                                  onClick={() => setActive.mutate(pendingActive)}
                                >
                                  {setActive.isPending
                                    ? "Saving…"
                                    : pendingActive.active
                                      ? "Confirm reactivate"
                                      : "Confirm deactivate"}
                                </Button>
                                <Button
                                  variant="ghost"
                                  className="px-2 py-1 text-xs"
                                  disabled={setActive.isPending}
                                  onClick={() => setPendingActive(null)}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="ghost"
                                className={
                                  "px-2 py-1 text-xs " +
                                  (u.active ? "text-red-600" : "")
                                }
                                disabled={setActive.isPending}
                                title={
                                  u.active
                                    ? "Lock this user out (their calls and history are kept)"
                                    : "Restore this user's access"
                                }
                                onClick={() =>
                                  setPendingActive({ id: u.id, active: !u.active })
                                }
                              >
                                {u.active ? "Deactivate" : "Reactivate"}
                              </Button>
                            ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
